import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { PromangRichTextEditor } from "../../../utils/PromangRichTextEditor";

import {

  addDays,

  avatarColorForId,

  DAY_LABELS,

  defaultDaySlots,

  fmtNo,

  getDaySchedule,
  planDayInWeek,
  planDayKey,
  planDayToDate,
  planDayWeekdayLabel,
  sortPlanDayKeys,

  hoursToDurationSlots,

  initials,

  parseTimeToSlot,

  slotToTime,

  slotsToHours,

  TOTAL_SLOTS,

  MAX_PLAN_HOURS,

} from "../../service/reports/agreement-redesign/schedule-utils";
import { isDateOnOrAfterEmploymentEnd } from "../../../utils/employmentEndDate";
import toast from "react-hot-toast";



/** Presets cover full visible day window (05:00–20:00 → up to 15 t). */
const PRESET_HOURS = Array.from(
  { length: Math.min(12, MAX_PLAN_HOURS) },
  (_, i) => i + 1
);



function Dot({ color }) {

  return (

    <span

      style={{

        display: "inline-block",

        width: 8,

        height: 8,

        borderRadius: 999,

        background: color,

        marginRight: 2,

      }}

    />

  );

}



function DayScheduleRow({ dateKey, weekStart, schedule, onScheduleChange, t, outsideWeek }) {
  const date = planDayToDate(dateKey, weekStart);
  const dateLabel = date ? fmtNo(date) : String(dateKey);
  const dayLabel = planDayWeekdayLabel(dateKey, weekStart);

  const fromVal = slotToTime(schedule.startSlot);

  const toVal = slotToTime(schedule.endSlot);

  const hours = slotsToHours(schedule.startSlot, schedule.endSlot);

  const roundedHours = Math.round(hours);
  const activePreset =
    hours >= 1 &&
    hours <= MAX_PLAN_HOURS &&
    Math.abs(hours - roundedHours) < 0.01 &&
    PRESET_HOURS.includes(roundedHours)
      ? roundedHours
      : null;



  const updateSlots = (startSlot, endSlot) => {

    const start = Math.max(0, startSlot);

    const end = clampEnd(start, endSlot);

    onScheduleChange({ startSlot: start, endSlot: end });

  };



  const setFromTime = (value) => {

    const slot = parseTimeToSlot(value);

    if (slot == null) return;

    const end =

      schedule.endSlot > slot

        ? schedule.endSlot

        : Math.min(slot + hoursToDurationSlots(1), TOTAL_SLOTS);

    updateSlots(slot, end);

  };



  const setToTime = (value) => {

    const slot = parseTimeToSlot(value);

    if (slot == null) return;

    const end = Math.max(slot, schedule.startSlot + 1);

    updateSlots(schedule.startSlot, Math.min(end, TOTAL_SLOTS));

  };



  const setPresetHours = (h) => {

    const span = hoursToDurationSlots(h);

    updateSlots(

      schedule.startSlot,

      Math.min(schedule.startSlot + span, TOTAL_SLOTS)

    );

  };



  return (

    <div className="rounded-lg border border-slate-200 bg-white p-3">

      <div className="flex items-baseline justify-between gap-2 mb-2">

        <div>

          <span className="text-xs font-semibold uppercase text-slate-500">

            {dayLabel}

          </span>

          <span className="ml-2 text-sm font-medium text-slate-900">{dateLabel}</span>

        </div>

        <span className="text-xs font-medium text-teal-800 tabular-nums">

          {hours} {t("work_order.plan_modal.hours_short")}

        </span>

      </div>

      <div className="flex flex-wrap items-center gap-2">

        <label className="flex items-center gap-1.5 text-xs text-slate-600">

          {t("work_order.plan_modal.time_from")}

          <input

            type="time"

            value={fromVal}

            onChange={(e) => setFromTime(e.target.value)}

            className="rounded-md border border-gray-300 px-2 py-1 text-sm"

          />

        </label>

        <label className="flex items-center gap-1.5 text-xs text-slate-600">

          {t("work_order.plan_modal.time_to")}

          <input

            type="time"

            value={toVal}

            onChange={(e) => setToTime(e.target.value)}

            className="rounded-md border border-gray-300 px-2 py-1 text-sm"

          />

        </label>

      </div>

      <div className="flex flex-wrap gap-1 mt-2">

        {PRESET_HOURS.map((h) => (

          <button

            key={h}

            type="button"

            onClick={() => setPresetHours(h)}

            className={`min-w-[2rem] rounded-md border px-2 py-1 text-xs font-semibold transition-colors ${

              activePreset === h

                ? "border-teal-600 bg-teal-600 text-white"

                : "border-slate-200 bg-slate-50 text-slate-800 hover:border-teal-400"

            }`}

          >

            {h}t

          </button>

        ))}

      </div>

    </div>

  );

}



function clampEnd(startSlot, endSlot) {

  return Math.max(startSlot + 1, Math.min(endSlot, TOTAL_SLOTS));

}



export default function PlanScheduleFormColumn({

  title,

  onTitleChange,

  showTitleField,

  description = "",
  onDescriptionChange = null,
  showDescriptionField = false,

  workOrderLabel,
  projectActivityLabel,
  schedulingMode = "project",
  simpleActivityMode = false,

  planningType = "resources",
  onPlanningTypeChange,
  hasExistingResources = false,

  employees,

  selectedEmployeeIds,

  onToggleEmployee,

  onSelectAllParticipants,

  onSelectAllEmployees,

  onClearEmployees,

  search,

  setSearch,

  estimatedHours,

  onEstimatedHoursChange,

  onApplyEstimatedHoursToAllDays,

  budgetedHoursHint,

  planSelection,

  onPlanSelectionChange,

  weekStart,

  weekLabel = null,

  onWeekChange = null,

  onGoToday = null,

}) {

  const { t } = useTranslation();

  const datesOnly = planningType === "dates_only";

  const days = [...(planSelection?.days || [])].sort(sortPlanDayKeys);
  const daysInWeek = days.filter((dayKey) => planDayInWeek(weekStart, dayKey));
  const daysOtherWeeks = days.filter((dayKey) => !planDayInWeek(weekStart, dayKey));



  const roundedEstimated = Math.round(estimatedHours);
  const activePreset =
    estimatedHours >= 1 &&
    estimatedHours <= MAX_PLAN_HOURS &&
    Math.abs(estimatedHours - roundedEstimated) < 0.01 &&
    PRESET_HOURS.includes(roundedEstimated)
      ? roundedEstimated
      : null;



  const updateDaySchedule = (dayKey, slots) => {
    onPlanSelectionChange({
      daySchedules: { [dayKey]: slots },
      ...(days[0] === dayKey
        ? { startSlot: slots.startSlot, endSlot: slots.endSlot }
        : {}),
    });
  };

  const removeDayKey = (dayKey) => {
    if (!days.includes(dayKey)) return;

    const nextDays = days.filter((d) => d !== dayKey);
    const daySchedules = { ...(planSelection.daySchedules || {}) };
    delete daySchedules[dayKey];

    const first = nextDays[0];
    const firstSlots = first != null ? daySchedules[first] : null;

    onPlanSelectionChange({
      days: nextDays,
      daySchedules,
      ...(firstSlots
        ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
        : {}),
    });
  };

  const toggleDay = (dayIndex) => {
    const dayKey = planDayKey(weekStart, dayIndex);
    const isRemoving = days.includes(dayKey);

    if (isRemoving) {
      removeDayKey(dayKey);
      return;
    }

    const blockedEmployee = (employees || []).find(
      (emp) =>
        selectedEmployeeIds?.includes(emp.id) &&
        isDateOnOrAfterEmploymentEnd(dayKey, emp)
    );
    if (blockedEmployee) {
      toast.error(
        t("employment.end_date.plan_blocked", {
          name: blockedEmployee.displayTitle || blockedEmployee.name || "",
          date: String(
            blockedEmployee.employment?.employment_ends ||
              blockedEmployee.employment?.end_date ||
              ""
          ).substring(0, 10),
        })
      );
      return;
    }

    const nextDays = [...days, dayKey].sort(sortPlanDayKeys);

    const daySchedules = { ...(planSelection.daySchedules || {}) };

    const template =
      getDaySchedule(planSelection, days[0]) ||
      (planSelection.startSlot != null
        ? getDaySchedule(
            {
              startSlot: planSelection.startSlot,
              endSlot: planSelection.endSlot,
              days: [dayKey],
            },
            dayKey
          )
        : null) ||
      defaultDaySlots(estimatedHours);

    daySchedules[dayKey] = { ...template };

    const first = nextDays[0];

    const firstSlots = first != null ? daySchedules[first] : null;

    onPlanSelectionChange({
      days: nextDays,
      daySchedules,
      ...(firstSlots
        ? { startSlot: firstSlots.startSlot, endSlot: firstSlots.endSlot }
        : {}),
    });
  };



  return (

    <div className="ra-form-col">

      {workOrderLabel ? (
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            {t("work_order.plan_modal.work_order")}
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">{workOrderLabel}</div>
        </div>
      ) : null}

      {projectActivityLabel && schedulingMode === "project_activity" && !workOrderLabel ? (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/60 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-700">
            {t("project_activity.label")}
          </div>
          <div className="text-sm font-medium text-slate-900 truncate">{projectActivityLabel}</div>
          {!simpleActivityMode ? (
            <p className="mt-1 text-xs text-violet-800/90">
              {t("project.plan.schedule.activity_plan_hint")}
            </p>
          ) : null}
        </div>
      ) : null}



      {onPlanningTypeChange ? (
        <div className="mb-4">
          <div className="ra-section-label">
            <Dot color="#f59e0b" /> {t("work_order.plan_modal.planning_type_section")}
          </div>
          <div className="ra-plan-modeswitch mt-1.5" role="group">
            <button
              type="button"
              className={!datesOnly ? "active" : ""}
              onClick={() => onPlanningTypeChange("resources")}
            >
              {t("work_order.plan_modal.planning_type_resources")}
            </button>
            <button
              type="button"
              className={datesOnly ? "active" : ""}
              onClick={() => onPlanningTypeChange("dates_only")}
            >
              {t("work_order.plan_modal.planning_type_dates_only")}
            </button>
          </div>
          <div className="ra-section-help mt-1.5">
            {datesOnly
              ? t("work_order.plan_modal.planning_type_dates_only_help")
              : t("work_order.plan_modal.planning_type_resources_help")}
          </div>
          {datesOnly && hasExistingResources ? (
            <p className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900">
              {t("work_order.plan_modal.dates_only_remove_warning")}
            </p>
          ) : null}
        </div>
      ) : null}

      {showTitleField ? (

        <div className="mb-4">

          <label className="ra-section-label">

            <Dot color="#2B6CFF" /> {t("work_order.plan_modal.plan_title")}

          </label>

          <input

            type="text"

            value={title}

            onChange={(e) => onTitleChange(e.target.value)}

            className="mt-1.5 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"

            placeholder={t("work_order.plan_modal.plan_title_placeholder")}

          />

          <p className="mt-1 text-xs text-slate-500">
            {t("work_order.plan_modal.plan_title_help")}
          </p>

        </div>

      ) : null}

      {showDescriptionField && onDescriptionChange ? (
        <div className="mb-4">
          <label className="ra-section-label">
            <Dot color="#64748b" /> {t("work_order.plan_modal.plan_description")}
          </label>
          <div className="mt-1.5">
            <PromangRichTextEditor
              value={description || ""}
              onChange={(html) => onDescriptionChange(html)}
              placeholder={t("work_order.plan_modal.plan_description_placeholder")}
              size="md"
              framed
              templatesEnabled={false}
            />
          </div>
        </div>
      ) : null}



      {!datesOnly ? (
      <div>

        <div className="ra-section-label">

          <Dot color="#1f8ce8" />{" "}
          {simpleActivityMode
            ? t("project.plan.schedule_activity.crew_section")
            : t("work_order.plan_modal.executing_section")}

        </div>

        {!simpleActivityMode ? (
          <div className="ra-section-help">{t("work_order.plan_modal.executing_help_multi")}</div>
        ) : null}



        {!simpleActivityMode ? (
          <div className="ra-section-help mb-2">
            {t("work_order.plan_modal.all_employees_hint")}
          </div>
        ) : null}

        {onSelectAllParticipants || onSelectAllEmployees || onClearEmployees ? (
          <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
            {onSelectAllParticipants ? (
              <button
                type="button"
                className="text-xs font-medium text-teal-700 hover:text-teal-900"
                onClick={onSelectAllParticipants}
              >
                {t("work_order.plan_modal.select_all_participants")}
              </button>
            ) : null}

            {onSelectAllEmployees ? (
              <button
                type="button"
                className="text-xs font-medium text-teal-700 hover:text-teal-900"
                onClick={onSelectAllEmployees}
              >
                {t("work_order.plan_modal.select_all_employees")}
              </button>
            ) : null}

            {onClearEmployees ? (
              <button
                type="button"
                className="text-xs font-medium text-slate-500 hover:text-slate-700"
                onClick={onClearEmployees}
              >
                {t("work_order.plan_modal.clear_employees")}
              </button>
            ) : null}
          </div>
        ) : null}



        <div className="ra-emp-search">

          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">

            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5" />

            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />

          </svg>

          <input

            placeholder={t("report.agreement.redesign.search_employee")}

            value={search}

            onChange={(e) => setSearch(e.target.value)}

          />

        </div>



        <div className="ra-emp-grid">

          {employees.map((e) => {

            const color = avatarColorForId(e.id);

            const isSelected = selectedEmployeeIds.includes(e.id);

            const isParticipant = e.__fromParticipant;

            return (

              <button

                key={e.id}

                type="button"

                className={`ra-emp-card${isSelected ? " selected" : ""}`}

                onClick={() => onToggleEmployee(e.id)}

              >

                <div className="ra-emp-avatar" style={{ background: color }}>

                  {initials(e.displayTitle || e.name)}

                </div>

                <div className="ra-emp-meta">

                  <div className="ra-emp-name">
                    {e.displayTitle || e.name}
                    {e.user_type_id === 3 && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        {t("employeeSelector.hired_badge")}
                      </span>
                    )}
                    {(e.employment?.employment_ends || e.employment?.end_date) && (
                      <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-800">
                        {t("employment.end_date.badge", {
                          date: String(
                            e.employment?.employment_ends || e.employment?.end_date
                          ).substring(0, 10),
                        })}
                      </span>
                    )}
                  </div>

                  <div className="ra-emp-role">

                    {isParticipant

                      ? t("work_order.plan_modal.participant")

                      : e.email || ""}

                  </div>

                </div>

                {isSelected ? (

                  <span className="ml-auto text-teal-600 text-xs font-bold">✓</span>

                ) : null}

              </button>

            );

          })}

          {employees.length === 0 ? (

            <div style={{ color: "var(--text-muted)", fontSize: 13, padding: 8 }}>

              {t("report.agreement.redesign.no_employees")}

            </div>

          ) : null}

        </div>

        {selectedEmployeeIds.length > 0 ? (

          <p className="mt-2 text-xs text-slate-500">

            {t("work_order.plan_modal.selected_employees_count", {

              count: selectedEmployeeIds.length,

            })}

          </p>

        ) : null}

      </div>
      ) : null}



      <div>

        <div className="ra-section-label">

          <Dot color="#16a34a" />{" "}
          {simpleActivityMode
            ? t("project.plan.schedule_activity.days_section")
            : t("work_order.plan_modal.days_section")}

        </div>

        {!simpleActivityMode ? (
          <div className="ra-section-help">{t("work_order.plan_modal.days_help")}</div>
        ) : null}
        {daysOtherWeeks.length > 0 ? (
          <p className="mt-1 text-xs text-slate-500">
            {t("work_order.plan_modal.multi_week_nav_hint")}
          </p>
        ) : null}

        {onWeekChange ? (
          <div className="ra-cal-nav mt-2">
            <button
              type="button"
              onClick={() => onWeekChange(-1)}
              aria-label={t("report.agreement.redesign.prev_week")}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M7.5 2L3.5 6l4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {onGoToday ? (
              <button
                type="button"
                onClick={onGoToday}
                style={{ borderColor: "var(--primary-200)", color: "var(--primary-700)" }}
              >
                {t("report.agreement.redesign.today")}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => onWeekChange(1)}
              aria-label={t("report.agreement.redesign.next_week")}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                <path
                  d="M4.5 2l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            {weekLabel ? <span className="ra-week-label">{weekLabel}</span> : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-1.5 mt-2">

          {DAY_LABELS.map((label, dayIndex) => {

            const selected = days.includes(planDayKey(weekStart, dayIndex));

            const d = addDays(weekStart, dayIndex);

            return (

              <button

                key={label}

                type="button"

                onClick={() => toggleDay(dayIndex)}

                className={`rounded-lg border px-2.5 py-1.5 text-sm font-medium transition-colors ${

                  selected

                    ? "border-teal-600 bg-teal-600 text-white"

                    : "border-slate-200 bg-white text-slate-700 hover:border-teal-400"

                }`}

              >

                <span className="block text-[10px] opacity-80">{label}</span>

                <span>{d.getDate()}</span>

              </button>

            );

          })}

        </div>

        {days.length > 0 ? (
          <>
            <p className="mt-2 text-xs text-slate-500">
              {t("work_order.plan_modal.selected_days_count", { count: days.length })}
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {days.map((dayKey) => {
                const date = planDayToDate(dayKey, weekStart);
                const dateLabel = date ? fmtNo(date) : dayKey;
                const weekdayLabel = planDayWeekdayLabel(dayKey, weekStart);
                const outsideWeek = !planDayInWeek(weekStart, dayKey);

                return (
                  <span
                    key={dayKey}
                    className="inline-flex items-center gap-0.5 rounded-lg border border-teal-200 bg-teal-50 py-1 pl-2 pr-1 text-xs font-medium text-teal-900"
                  >
                    <span>
                      {weekdayLabel} {dateLabel}
                      {outsideWeek ? (
                        <span className="ml-1 text-[10px] font-normal text-teal-700/80">
                          ({t("work_order.plan_modal.other_week_badge")})
                        </span>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeDayKey(dayKey)}
                      className="rounded p-0.5 text-teal-700 hover:bg-teal-200/80 hover:text-teal-950"
                      title={t("work_order.plan_modal.remove_day")}
                      aria-label={t("work_order.plan_modal.remove_day")}
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          </>
        ) : null}

      </div>



      {days.length > 0 && !datesOnly ? (

        <div>

          <div className="ra-section-label">

            <Dot color="#7c3aed" /> {t("work_order.plan_modal.per_day_section")}

          </div>

          {!simpleActivityMode ? (
            <div className="ra-section-help">{t("work_order.plan_modal.per_day_help")}</div>
          ) : null}



          <div className="mt-2 mb-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/80 p-2.5">

            <div className="text-xs font-medium text-slate-600 mb-1.5">

              {t("work_order.plan_modal.default_hours_label")}

            </div>

            <div className="flex flex-wrap gap-1">

              {PRESET_HOURS.map((h) => (

                <button

                  key={h}

                  type="button"

                  onClick={() => onEstimatedHoursChange(h)}

                  className={`min-w-[2rem] rounded-md border px-2 py-1 text-xs font-semibold ${

                    activePreset === h

                      ? "border-teal-600 bg-teal-600 text-white"

                      : "border-slate-200 bg-white text-slate-800 hover:border-teal-400"

                  }`}

                >

                  {h}t

                </button>

              ))}

            </div>

            {days.length > 1 ? (

              <button

                type="button"

                className="mt-2 text-xs font-medium text-teal-700 hover:text-teal-900"

                onClick={onApplyEstimatedHoursToAllDays}

              >

                {t("work_order.plan_modal.apply_hours_all_days")}

              </button>

            ) : null}

            {budgetedHoursHint != null && budgetedHoursHint > 0 ? (

              <p className="mt-1 text-[11px] text-slate-500">

                {simpleActivityMode
                  ? t("project.plan.schedule_activity.budget_hours_hint", {
                      hours: budgetedHoursHint,
                    })
                  : t("work_order.plan_modal.estimated_hours_from_wo", {
                      hours: budgetedHoursHint,
                    })}

              </p>

            ) : null}

          </div>



          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-0.5">
            {daysOtherWeeks.length > 0 ? (
              <>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  {t("work_order.plan_modal.per_day_other_weeks", {
                    count: daysOtherWeeks.length,
                  })}
                </p>
                {daysOtherWeeks.map((dayKey) => {
                  const schedule =
                    getDaySchedule(planSelection, dayKey) ||
                    defaultDaySlots(estimatedHours);
                  return (
                    <DayScheduleRow
                      key={dayKey}
                      dateKey={dayKey}
                      weekStart={weekStart}
                      schedule={schedule}
                      outsideWeek
                      onScheduleChange={(slots) => updateDaySchedule(dayKey, slots)}
                      t={t}
                    />
                  );
                })}
              </>
            ) : null}
            {daysInWeek.length > 0 && daysOtherWeeks.length > 0 ? (
              <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {t("work_order.plan_modal.per_day_this_week")}
              </p>
            ) : null}
            {daysInWeek.map((dayKey) => {
              const schedule =
                getDaySchedule(planSelection, dayKey) ||
                defaultDaySlots(estimatedHours);

              return (
                <DayScheduleRow
                  key={dayKey}
                  dateKey={dayKey}
                  weekStart={weekStart}
                  schedule={schedule}
                  onScheduleChange={(slots) => updateDaySchedule(dayKey, slots)}
                  t={t}
                />
              );
            })}
          </div>

        </div>

      ) : null}



      {!simpleActivityMode && !datesOnly ? (
        <div>
          <div className="ra-section-label">
            <Dot color="#94a3b8" /> {t("work_order.plan_modal.schedule_section")}
          </div>
          <div className="ra-section-help">{t("work_order.plan_modal.schedule_help_calendar")}</div>
          <div className="ra-timechip empty mt-1">
            <div className="tc-hint text-xs text-slate-500">
              {t("report.agreement.redesign.drag_hint")}{" "}
              <b>{t("report.agreement.redesign.drag_hint_bold")}</b>{" "}
              {t("work_order.plan_modal.drag_days_hint")}
            </div>
          </div>
        </div>
      ) : null}

    </div>

  );

}

